import React, { useEffect, useState } from 'react';
import { Upload, Download, Eye, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { cogsAPI } from '../../services/api';

// Import a sheet with Article Name, Style, Cost Price, Freight for one store, all stores of a platform,
// or every store on every platform. Preview first; nothing changes until "Apply".
const PLATFORM_LABEL = { amazon: 'Amazon', ebay: 'eBay', shopify: 'Shopify' };
const OPTION = { color: '#111827', backgroundColor: '#ffffff' };      // the open list is white, so dark text
const GROUP = { color: '#6b7280', backgroundColor: '#ffffff', fontStyle: 'normal' };

function findColumn(headers, patterns) {
  const i = headers.findIndex(h => patterns.some(p => p.test(String(h || '').trim())));
  return i;
}

export default function CostImport({ rates, onDone }) {
  const [stores, setStores] = useState([]);
  const [scope, setScope] = useState('all');
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState('');
  const [recalc, setRecalc] = useState(false);
  const [recalcFrom, setRecalcFrom] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { cogsAPI.stores().then(r => setStores(r.data.stores || [])).catch(() => {}); }, []);

  const scopeValue = () => {
    if (scope === 'all') return { platform: 'all' };
    const [platform, id] = scope.split(':');
    return id ? { platform, store_id: Number(id) } : { platform };
  };

  const readFile = (e) => {
    const file = e.target.files?.[0];
    setPreview(null);
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const aoa = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: '' });
        const headerRow = aoa.findIndex(r => r.some(c => /article/i.test(String(c))) && r.some(c => /style/i.test(String(c))));
        if (headerRow < 0) return toast.error('Could not find the "Article Name" and "Style" columns in the first sheet');
        const h = aoa[headerRow];
        const ai = findColumn(h, [/^article/i]), si = findColumn(h, [/^style/i]);
        const ci = findColumn(h, [/cost/i, /rate/i, /price/i]), fi = findColumn(h, [/freight/i]);
        if (ci < 0) return toast.error('Could not find the "Cost Price" column');
        const data = aoa.slice(headerRow + 1).map(r => ({ article: r[ai], style: r[si], cost: r[ci], freight: fi >= 0 ? r[fi] : '' }))
          .filter(r => String(r.article).trim() || String(r.style).trim());
        setRows(data);
        setFileName(file.name);
        toast.info(`${data.length} row(s) read from ${file.name}`);
      } catch (err) {
        toast.error('Could not read that file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const aoa = [['Article Name', 'Style', 'Cost Price', 'Freight'],
      ...(rates || []).filter(r => r.products > 0).map(r => [r.article, r.style, r.rate ?? '', r.freight ?? ''])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 22 }, { wch: 16 }, { wch: 12 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Costs');
    XLSX.writeFile(wb, 'Cost Update Template.xlsx');
  };

  const doPreview = async () => {
    if (!rows?.length) return toast.error('Choose a cost sheet first');
    setBusy(true);
    try { setPreview((await cogsAPI.importPreview(scopeValue(), rows)).data); }
    catch (err) { toast.error(err.response?.data?.error || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const doApply = async () => {
    if (recalc && !recalcFrom) return toast.error('Pick the date to recalculate past orders from');
    const scopeText = scope === 'all' ? 'ALL platforms and stores' : scopeLabel(scope);
    if (!window.confirm(`Update costs for ${scopeText}?\n\n${preview.distinct_skus} SKU cost(s) will change${recalc ? `, and past orders from ${recalcFrom} will be recalculated` : ''}.`)) return;
    setBusy(true);
    try {
      const r = (await cogsAPI.importApply(scopeValue(), rows, recalc ? recalcFrom : null)).data;
      const o = r.order_lines_recalculated || {};
      toast.success(`Saved ${r.rates_saved} rate(s), updated ${r.skus_updated} SKU cost(s)` +
        (recalc ? `, recalculated ${(o.amazon || 0) + (o.ebay || 0) + (o.shopify || 0)} order line(s)` : ''));
      setPreview(null); setRows(null); setFileName('');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const scopeLabel = (v) => {
    if (v === 'all') return 'All platforms & stores';
    const [platform, id] = v.split(':');
    if (!id) return `All ${PLATFORM_LABEL[platform]} stores`;
    const s = stores.find(x => x.platform === platform && String(x.store_id) === id);
    return `${PLATFORM_LABEL[platform]} · ${s ? s.name : id}`;
  };

  const select = 'bg-white/10 border border-white/20 rounded px-3 py-2 text-white';
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
      <h2 className="text-white font-semibold mb-1 flex items-center gap-2"><Upload size={18} /> Import cost sheet</h2>
      <p className="text-gray-400 text-sm mb-4">
        Sheet columns: <b>Article Name</b>, <b>Style</b>, <b>Cost Price</b>, <b>Freight</b>. Every product of that Article + Style in the chosen
        stores gets Cost Price + Freight as its cost, and the rate is used in the Monthly Reports.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select className={select} value={scope} onChange={e => { setScope(e.target.value); setPreview(null); }}>
          <option style={OPTION} value="all">All platforms & stores</option>
          <optgroup style={GROUP} label="All stores of one platform">
            <option style={OPTION} value="amazon">All Amazon stores</option>
            <option style={OPTION} value="ebay">All eBay stores</option>
            <option style={OPTION} value="shopify">Shopify</option>
          </optgroup>
          <optgroup style={GROUP} label="One store">
            {stores.filter(s => s.platform !== 'shopify').map(s => (
              <option style={OPTION} key={`${s.platform}:${s.store_id}`} value={`${s.platform}:${s.store_id}`}>{PLATFORM_LABEL[s.platform]} · {s.name}</option>
            ))}
          </optgroup>
        </select>
        <label className="px-4 py-2 rounded bg-white/10 text-white cursor-pointer flex items-center gap-2">
          <Upload size={16} /> {fileName || 'Choose sheet (.xlsx / .csv)'}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={readFile} />
        </label>
        <button onClick={downloadTemplate} className="px-3 py-2 rounded bg-white/10 text-white text-sm flex items-center gap-2"><Download size={14} /> Template</button>
      </div>

      <label className="flex flex-wrap items-center gap-2 text-sm text-gray-300 mb-4">
        <input type="checkbox" checked={recalc} onChange={e => setRecalc(e.target.checked)} />
        Also correct cost and profit on orders already in the software, from
        <input type="date" value={recalcFrom} onChange={e => setRecalcFrom(e.target.value)} disabled={!recalc}
               className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white [color-scheme:dark] disabled:opacity-40" />
      </label>

      <div className="flex gap-2">
        <button onClick={doPreview} disabled={busy || !rows} className="px-4 py-2 rounded bg-white/10 text-white font-semibold flex items-center gap-2 disabled:opacity-50">
          <Eye size={16} /> {busy && !preview ? 'Checking…' : 'Preview'}
        </button>
        <button onClick={doApply} disabled={busy || !preview} className="px-4 py-2 rounded bg-white text-gray-900 font-semibold flex items-center gap-2 disabled:opacity-50">
          <Check size={16} /> Apply
        </button>
      </div>

      {preview && (
        <div className="mt-5 text-sm">
          <p className="text-white mb-2">
            {preview.rates} rate(s) in the sheet → <b>{preview.distinct_skus}</b> SKU cost(s) would change in <b>{scopeLabel(scope)}</b>:
          </p>
          <div className="grid sm:grid-cols-2 gap-1 mb-3">
            {preview.stores.map(s => (
              <div key={`${s.platform}${s.store_id}`} className="flex justify-between bg-white/5 rounded px-3 py-1">
                <span className="text-gray-300">{PLATFORM_LABEL[s.platform]} · {s.store}</span><span className="text-white">{s.skus} SKUs</span>
              </div>
            ))}
          </div>
          {preview.shared_sku_conflicts > 0 && (
            <p className="text-yellow-300 mb-2">{preview.shared_sku_conflicts} SKU code(s) are used on two stores with different costs — a SKU code holds one cost, so the last one wins.</p>
          )}
          {preview.problems.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
              <p className="text-red-300 mb-1">{preview.problems.length} row(s) need attention (other rows are fine):</p>
              <ul className="text-gray-300 list-disc ml-5 max-h-40 overflow-auto">
                {preview.problems.map((p, i) => <li key={i}>Row {p.row}{p.article ? ` — ${p.article} / ${p.style}` : ''}: {p.issue}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
